import { createContext, useContext, useState, ReactNode } from "react";
import ConfirmDialog from "../components/confirm-dialog";

/**
 * Copy for every confirm popup. Each entry says what the action actually
 * does, so the dialog is never just "are you sure?".
 */
const confirmationModelData = [
  {
    type: "delete",
    title: "Delete this chase?",
    desc: "The chase and its renewal history will be removed. This cannot be undone.",
    button: "Delete",
    tone: "destructive" as const,
  },
  {
    type: "manual",
    title: "Set this chase to manual?",
    desc: "Automated chasing stops. An agent takes over contacting this customer about their renewal.",
    button: "Set to Manual",
    tone: "default" as const,
  },
  {
    type: "lapse",
    title: "Set this chase to lapse?",
    desc: "Chasing stops and the policy is marked as lapsed. The customer will not receive any further renewal invites.",
    button: "Set to Lapse",
    tone: "default" as const,
  },
  {
    type: "confirmation",
    title: "Are you sure you want to proceed?",
    desc: "This cannot be undone.",
    button: "Confirm",
    tone: "default" as const,
  },
];

interface IConfirmationModel {
  onClick: () => void;
  type: "delete" | "confirmation" | "manual" | "lapse";
  loading?: boolean;
}

type DeleteContextType = {
  showModal: ({ onClick, type, loading }: IConfirmationModel) => void;
  hideModal: () => void;
};

const ConfirmationContext = createContext<DeleteContextType | undefined>(
  undefined
);

export const useConfirmationModel = () => {
  const context = useContext(ConfirmationContext);
  return context!;
};

export const ConfirmationModelProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<IConfirmationModel | null>(null);
  const modelData = confirmationModelData?.find(
    (cmd) => cmd.type === data?.type
  );

  const showModal = ({ onClick, type, loading }: IConfirmationModel) => {
    setData({ onClick, type, loading });
    setVisible(true);
  };

  const hideModal = () => {
    setVisible(false);
    setData(null);
  };

  return (
    <ConfirmationContext.Provider value={{ showModal, hideModal }}>
      {children}
      {visible && (
        <ConfirmDialog
          title={modelData?.title ?? ""}
          description={modelData?.desc}
          confirmLabel={modelData?.button ?? "Confirm"}
          tone={modelData?.tone}
          loading={data?.loading}
          onCancel={hideModal}
          onConfirm={() => {
            setData((prev) => ({ ...prev, loading: true }));
            data?.onClick();
          }}
        />
      )}
    </ConfirmationContext.Provider>
  );
};
